import React, { MutableRefObject, ReactElement, useContext } from 'react'

import FormRow from '../../Common/Components/FormRow'
import P from '../../Common/Components/P'
import Checkbox from '../../Common/Components/Checkbox'

import { LanguageContext } from '../../Common/Contexts'

import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { Select, SelectOptions } from '../../Common/Components/Selects'
import TextInput from '../../Common/Components/TextInput'
import { DisplayedAsOptions as DisplayedAsOptionsLanguage, DisplayOptions } from '../../../../i18n'
import ChartFields from './ChartFields'
import ReportContentRowsSettings from '../Common/ShowRowsSettingsCheckboxes'
import { ListReportDisplayedAsValues } from '../../Common/Constants/ReportDisplayedAsValues'
import { ZOOM_LEVEL_DEFAULT_VALUE } from '../Constants'

// CSS imports. Commented out because of current build process.
// import './callStateThresholds.scss'

/**
 * Component properties type.
 */
type Props = {
    isPersonalDashboard: MutableRefObject<boolean>
    displayedAsListReport: ListReportDisplayedAsValues
    setDisplayedAsListReport: (value: ListReportDisplayedAsValues) => void
    zoomLevel: string
    setZoomLevel: (value: string) => void
    isZoomLevelCustom: boolean
    setIsZoomLevelCustom: (value: boolean) => void
    chartType: number
    setChartType: (value: number) => void
    chartProvider: number
    setChartProvider: (value: number) => void
    isShowingHeaderRowEnabled: boolean
    setIsShowingHeaderRowEnabled: (value: boolean) => void
    isShowingFooterRowEnabled: boolean
    setIsShowingFooterRowEnabled: (value: boolean) => void
    isShowingContentRowsEnabled: boolean
    setIsShowingContentRowsEnabled: (value: boolean) => void
    isShowingSpacerRowsEnabled: boolean
    setIsShowingSpacerRowsEnabled: (value: boolean) => void
    isShowingLargeBLFToolTipsEnabled: boolean
    setIsShowingLargeBLFToolTipsEnabled: (value: boolean) => void
    isAutomaticTextTruncationEnabled: boolean
    setIsAutomaticTextTruncationEnabled: (value: boolean) => void
}

/**
 * Call state thresholds section in Alarms tab of report configuration modal window.
 *
 * @param isPersonalDashboard Is it personal dashboard mode?
 * @param displayedAsListReport Selected display option for list reports.
 * @param setDisplayedAsListReport Method that sets the selected display type.
 * @param zoomLevel Zoom level percentage value.
 * @param setZoomLevel Method that sets the zoom level value.
 * @param isZoomLevelCustom Zoom level auto checkbox.
 * @param setIsZoomLevelCustom Method that sets the zoom auto checkbox.
 * @param chartType Selected chart style option for list reports.
 * @param setChartType Method that sets the selected chart style.
 * @param chartProvider Selected chart provider option for list reports.
 * @param setChartProvider Method that sets the selected chart provider.
 * @param setIsShowingHeaderRowEnabled Setter method of the show header checkbox.
 * @param isShowingFooterRowEnabled Boolean defining the state of the show footer checkbox.
 * @param setIsShowingFooterRowEnabled Setter method of the show footer checkbox.
 * @param isShowingContentRowsEnabled Boolean defining the state of the show content checkbox.
 * @param setIsShowingContentRowsEnabled Setter method of the show content checkbox.
 * @param isShowingSpacerRowsEnabled Boolean defining the state of the show spacer checkbox.
 * @param setIsShowingSpacerRowsEnabled Setter method of the show spacer checkbox.
 * @param isShowingLargeBLFToolTipsEnabled Boolean defining the state of the show BLF tooltips checkbox.
 * @param setIsShowingLargeBLFToolTipsEnabled Setter method of the show BLF tooltips checkbox.
 * @param isAutomaticTextTruncationEnabled Boolean defining the state of the automatic text truncation checkbox.
 * @param setIsAutomaticTextTruncationEnabled Setter method of the automatic text truncation checkbox.
 */
const ListReportDisplayOptions: React.FC<Props> = ({
    isPersonalDashboard,
    displayedAsListReport,
    setDisplayedAsListReport,
    isZoomLevelCustom,
    setIsZoomLevelCustom,
    zoomLevel,
    setZoomLevel,
    chartType,
    setChartType,
    chartProvider,
    setChartProvider,
    isShowingHeaderRowEnabled,
    setIsShowingHeaderRowEnabled,
    isShowingFooterRowEnabled,
    setIsShowingFooterRowEnabled,
    isShowingContentRowsEnabled,
    setIsShowingContentRowsEnabled,
    isShowingSpacerRowsEnabled,
    setIsShowingSpacerRowsEnabled,
    isShowingLargeBLFToolTipsEnabled,
    setIsShowingLargeBLFToolTipsEnabled,
    isAutomaticTextTruncationEnabled,
    setIsAutomaticTextTruncationEnabled
}: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle('DisplayOptions')
    language.loadBundle('DisplayedAsOptions')

    const isDetailedTableView =
        displayedAsListReport === ListReportDisplayedAsValues.DISPLAYED_AS_LISTREPORT_OPTION_VALUE_DETAILEDTABLEVIEW
    const isChartView =
        displayedAsListReport === ListReportDisplayedAsValues.DISPLAYED_AS_LISTREPORT_OPTION_VALUE_CHART_VIEW
    const isBLFView = !isDetailedTableView && !isChartView

    const nonPDOptions = (): SelectOptions<number> => {
        if (isPersonalDashboard.current) {
            return []
        }
        return [
            {
                label: `${language?.get(DisplayedAsOptionsLanguage.DisplayasoptionsChartview)}`,
                value: ListReportDisplayedAsValues.DISPLAYED_AS_LISTREPORT_OPTION_VALUE_CHART_VIEW
            }
        ]
    }

    const displayedAsListReportOptions: SelectOptions<ListReportDisplayedAsValues> = [
        {
            label: `${language?.get(DisplayedAsOptionsLanguage.DisplayasoptionsDetailedtableview)}`,
            value: ListReportDisplayedAsValues.DISPLAYED_AS_LISTREPORT_OPTION_VALUE_DETAILEDTABLEVIEW
        },
        {
            label: `${language?.get(DisplayedAsOptionsLanguage.DisplayasoptionsBlfviewhorizontal)}`,
            value: ListReportDisplayedAsValues.DISPLAYED_AS_LISTREPORT_OPTION_VALUE_BLFVIEW_HORIZONTAL
        },
        {
            label: `${language?.get(DisplayedAsOptionsLanguage.DisplayasoptionsBlfviewvertical)}`,
            value: ListReportDisplayedAsValues.DISPLAYED_AS_LISTREPORT_OPTION_VALUE_BLFVIEW_VERTICAL
        },
        ...nonPDOptions()
    ]

    const setZoomLevelOnToggle = (): void => {
        setIsZoomLevelCustom(!isZoomLevelCustom)
        setZoomLevel('')
    }

    return (
        <React.Fragment>
            <FormRow isInline className={'displayed-as'}>
                <Select
                    label={language?.get(DisplayOptions.DisplayedAs)}
                    options={displayedAsListReportOptions}
                    value={displayedAsListReport}
                    handleChange={setDisplayedAsListReport}
                />
            </FormRow>
            {(isDetailedTableView || isBLFView) && (
                <FormRow isInline className={'zoom-level'}>
                    <TextInput
                        value={zoomLevel}
                        onChange={setZoomLevel}
                        labelLeft={language.get(DisplayOptions.ZoomLevel)}
                        placeholder={String(ZOOM_LEVEL_DEFAULT_VALUE)}
                        disabled={!isZoomLevelCustom}
                        adornmentEnd={language.get(DisplayOptions.PercentageUnit)}
                    />

                    <Checkbox
                        className='left-aligned-checkbox auto'
                        labelLeft={language.get(DisplayOptions.LabelAuto)}
                        onChange={setZoomLevelOnToggle}
                        isChecked={!isZoomLevelCustom}
                    />
                </FormRow>
            )}
            {isChartView && (
                <ChartFields
                    chartType={chartType}
                    setChartType={setChartType}
                    chartProvider={chartProvider}
                    setChartProvider={setChartProvider}
                />
            )}

            {isDetailedTableView && (
                <React.Fragment>
                    <ReportContentRowsSettings
                        isShowingHeaderRowEnabled={isShowingHeaderRowEnabled}
                        setIsShowingHeaderRowEnabled={setIsShowingHeaderRowEnabled}
                        isShowingFooterRowEnabled={isShowingFooterRowEnabled}
                        setIsShowingFooterRowEnabled={setIsShowingFooterRowEnabled}
                        isShowingContentRowsEnabled={isShowingContentRowsEnabled}
                        setIsShowingContentRowsEnabled={setIsShowingContentRowsEnabled}
                        isShowingSpacerRowsEnabled={isShowingSpacerRowsEnabled}
                        setIsShowingSpacerRowsEnabled={setIsShowingSpacerRowsEnabled}
                    />
                </React.Fragment>
            )}

            {isBLFView && (
                <React.Fragment>
                    <P>{language?.get(DisplayOptions.LocalBrowserPerformance)}</P>
                    <FormRow isInline className={'blf-view'}>
                        <Checkbox
                            className='left-aligned-checkbox show-blf-tooltips'
                            labelLeft={language.get(DisplayOptions.ShowBLFTooltips)}
                            onChange={setIsShowingLargeBLFToolTipsEnabled}
                            isChecked={isShowingLargeBLFToolTipsEnabled}
                        />
                        <Checkbox
                            className='left-aligned-checkbox automatic-text-truncation-adjustment'
                            labelLeft={language.get(DisplayOptions.AutomaticTextTruncationAdjustment)}
                            onChange={setIsAutomaticTextTruncationEnabled}
                            isChecked={isAutomaticTextTruncationEnabled}
                        />
                    </FormRow>
                </React.Fragment>
            )}
        </React.Fragment>
    )
}

export default ListReportDisplayOptions
